// Serviço de redefinição de senha, em 2 etapas:
//
// 1. Solicitação: POST /api/auth/reset-senha/solicitar  { cpfcnpj }
//    -> { success, message, data: { email, expiraEmMinutos } }
//    (código de 6 dígitos enviado por e-mail, não um link)
// 2. Confirmação:  POST /api/auth/reset-senha/confirmar  { cpfcnpj, codigo, novaSenha }
//    -> { success, message }
//
// Sem acesso ao e-mail de recuperação (ou sem e-mail cadastrado), o usuário
// troca o e-mail antes da etapa 1 respondendo perguntas sobre o cadastro:
//    POST /api/auth/email-recuperacao  { cpfcnpj, novoEmail, ...respostas }
//    -> { success, message, data: { email } }
//    As perguntas dependem do perfil gravado no banco. Enviar só cpfcnpj +
//    novoEmail devolve MISSING_FIELDS com a lista delas, sem contar tentativa.

import authApi from '../../../services/authApi';
import logger from '../../../utils/logger';

class ResetSenhaService {

  async solicitarReset(cpfCnpj) {
    try {
      const response = await authApi.post('/auth/reset-senha/solicitar', {
        cpfcnpj: cpfCnpj
      });
      return response.data;
    } catch (error) {
      logger.error('Erro ao solicitar reset:', error);
      throw this._handleError(error);
    }
  }

  async confirmarReset(cpfCnpj, codigo, novaSenha) {
    try {
      const response = await authApi.post('/auth/reset-senha/confirmar', {
        cpfcnpj: cpfCnpj,
        codigo,
        novaSenha
      });
      return response.data;
    } catch (error) {
      logger.error('Erro ao confirmar reset:', error);
      throw this._handleError(error);
    }
  }

  async trocarEmailRecuperacao(cpfCnpj, novoEmail, respostas = {}) {
    try {
      const response = await authApi.post('/auth/email-recuperacao', {
        cpfcnpj: cpfCnpj,
        novoEmail,
        ...respostas
      });
      return response.data;
    } catch (error) {
      logger.error('Erro ao trocar e-mail de recuperação:', error);
      throw this._handleError(error);
    }
  }

  /**
   * Mapeia códigos de erro do backend (data.code) para objetos padronizados
   * { type, message, status } que o frontend usa para exibir mensagens amigáveis.
   */
  _handleError(error) {
    if (error.response) {
      const { status, data } = error.response;

      switch (data.code) {
        case 'USER_NOT_FOUND':
          return {
            type: 'user_not_found',
            message: data.message || 'CPF/CNPJ não encontrado',
            status
          };

        case 'USER_INACTIVE':
          return {
            type: 'inactive_user',
            message: data.message || 'Usuário inativo',
            status
          };

        case 'RECOVERY_EMAIL_NOT_FOUND':
          return {
            type: 'no_recovery_email',
            message: data.message || 'Usuário sem e-mail de recuperação cadastrado',
            status
          };

        case 'RESET_NOT_REQUESTED':
          return {
            type: 'reset_not_requested',
            message: data.message || 'Nenhum código pendente. Solicite um novo.',
            status
          };

        case 'CODE_EXPIRED':
          return {
            type: 'code_expired',
            message: data.message || 'Código expirado. Solicite um novo.',
            status
          };

        case 'INVALID_CODE':
          return {
            type: 'invalid_code',
            message: data.message || 'Código incorreto',
            status
          };

        case 'TOO_MANY_ATTEMPTS':
          return {
            type: 'too_many_attempts',
            message: data.message || 'Muitas tentativas incorretas. Solicite um novo código.',
            status
          };

        case 'MISSING_FIELDS':
          return {
            type: 'missing_fields',
            message: data.message || 'Campos obrigatórios faltando',
            missingFields: data.missingFields || [],
            status
          };

        case 'INVALID_EMAIL':
          return {
            type: 'invalid_email',
            message: data.message || 'E-mail inválido',
            status
          };

        case 'INVALID_DATE':
          return {
            type: 'invalid_date',
            message: data.message || 'Data inválida. Use o formato DD/MM/AAAA',
            status
          };

        case 'DATA_MISMATCH':
          return {
            type: 'data_mismatch',
            message: data.message || 'Os dados informados não conferem com o cadastro.',
            tentativasRestantes: data.tentativasRestantes,
            status
          };

        case 'ACCOUNT_BLOCKED':
          return {
            type: 'account_blocked',
            message: data.message || 'Número máximo de tentativas excedido. Seu acesso foi bloqueado; procure a OAB/MS para reativá-lo.',
            status
          };

        case 'RECOVERY_EMAIL_CHANGE_UNAVAILABLE':
          return {
            type: 'recovery_email_change_unavailable',
            message: data.message || 'Não é possível alterar o e-mail de recuperação deste cadastro pela internet. Procure a OAB/MS.',
            status
          };

        default:
          return {
            type: 'server_error',
            message: data.message || 'Erro no servidor',
            status
          };
      }
    }

    return {
      type: 'network_error',
      message: 'Erro de conexão. Verifique sua internet.',
      status: 0
    };
  }
}

export const resetSenhaService = new ResetSenhaService();
